/*
 * Copyright (c) 2022 NEXTWELL INTL SRL. All Rights Reserved.
 * This software is the proprietary information of NEXTWELL INTL SRL.
 * Use is subject to license and non-disclosure terms
 */

import {
  ChangeDetectionStrategy,
  Component,
  forwardRef,
  inject,
  input,
  model,
  OnInit,
  output,
  signal
} from '@angular/core';
import { FormControl, NG_VALIDATORS, NG_VALUE_ACCESSOR } from '@angular/forms';
import { AbstractControlValueAccessorControl } from '@irian-ro/shared-components-module';
import { SelectItemsResourceService } from '../select-items-resource.service';
import { SelectItemsVoModel } from '@well/common-libs/select-items/domain';

@Component({
  selector: 'well-select-items-dropdown',
  templateUrl: './select-items-dropdown.component.html',
  styleUrls: ['./select-items-dropdown.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SelectItemsDropdownComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => SelectItemsDropdownComponent),
      multi: true
    }
  ]
})
export class SelectItemsDropdownComponent extends AbstractControlValueAccessorControl implements OnInit {
  selectedItem = signal<SelectItemsVoModel | undefined>(undefined);
  items = signal<SelectItemsVoModel[] | undefined>(undefined);
  required = input<boolean>(false);
  readonly = input<boolean>(false);
  showClear = input<boolean>(false);
  isFilter = input<boolean>(false);
  label = input<string>('');
  floatLabelVariant = input<'in' | 'over' | 'on'>('on');
  field = input<string | undefined>(undefined);
  appendTo = input<string | undefined>(undefined);
  style = input<any | undefined>(undefined);
  scrollHeight = input<string>('200px');
  clear = output<void>();
  disabled = model<boolean>(false);
  private _registerOnChange = signal<any | undefined>(undefined);
  private value = signal<string | undefined>(undefined);
  selectItemsResourceService = inject(SelectItemsResourceService);

  constructor() {
    super();
  }

  ngOnInit() {
    this.selectItemsResourceService.getSelectItemsByField(this.field()).subscribe((res) => {
      this.items.set(res.data);
      const value = this.value();
      if (value) {
        this.selectedItem.set(this.items().filter((item) => item.key === value)[0]);
      }
    });
  }

  registerOnChange(fn: any): void {
    this._registerOnChange.set(fn);
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }

  writeValue(key: string): void {
    if (key) {
      this.value.set(key);
      const items = this.items();
      if (items) {
        this.selectedItem.set(this.items().filter((item) => item.key === this.value())[0]);
      }
    } else {
      this.value.set(null);
      this.selectedItem.set(null);
    }
  }

  change(event: any) {
    this._registerOnChange()(event.value?.key);
  }

  protected createForm() {
    this.form = new FormControl(null);
  }

  onClear() {
    this.clear.emit();
  }
}

